import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Star, Quote, Heart, ThumbsUp, Smile } from 'lucide-react';

export const TestimonialsSection = () => {
  const highlights = [
    {
      icon: Heart,
      title: 'Freundliche Atmosphäre',
      description: 'Patienten schätzen besonders die warme und einladende Atmosphäre.',
    },
    {
      icon: ThumbsUp,
      title: 'Individuelle Betreuung',
      description: 'Alle Patienten erhalten eine auf Ihre Bedürfnisse abgestimmte, persönliche Therapie.',
    },
    {
      icon: Smile,
      title: 'Kompetente Therapie',
      description: 'Professionelle Behandlung auf aktuellem Stand der logopädischen Wissenschaft.',
    },
  ];

  return (
    <section id="bewertungen" className="section-padding bg-soft-gradient relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-1/4 w-72 h-72 bg-secondary/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto container-padding relative">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block text-primary text-sm font-body font-medium tracking-wide uppercase mb-4">
            Patientenstimmen
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-heading mb-6">
            Was Patienten sagen
          </h2>
          <p className="text-body font-body text-lg leading-relaxed">
            Das Feedback meiner Patienten bestätigt mich in meiner Arbeit und motiviert mich, 
            jeden Tag mein Bestes zu geben.
          </p>
        </div>

        {/* Main Testimonial Card */}
        <Card className="max-w-4xl mx-auto mb-16 bg-card border-border shadow-elegant overflow-hidden">
          <CardContent className="p-8 lg:p-12 relative">
            {/* Quote icon */}
            <div className="absolute top-6 right-6 lg:top-8 lg:right-8">
              <Quote className="w-12 h-12 lg:w-16 lg:h-16 text-primary/10" />
            </div>
            
            <div className="relative">
              {/* Stars */}
              <div className="flex items-center gap-1 mb-6">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-primary fill-primary" />
                ))}
              </div>
              
              {/* Quote */}
              <blockquote className="mb-8">
                <p className="text-heading font-heading text-xl lg:text-2xl font-medium leading-relaxed italic">
&bdquo;Patienten schätzen besonders die freundliche Atmosphäre, 
                  die individuelle Betreuung und die kompetente Therapie in meiner Praxis.&ldquo;
                </p>
              </blockquote>
              
              {/* Attribution */}
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <Heart className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-heading font-body font-medium">Patientenfeedback</p>
                  <p className="text-caption text-sm">Zusammenfassung öffentlicher Bewertungen</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Highlights Grid */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {highlights.map((highlight, index) => (
            <Card 
              key={highlight.title}
              className="bg-card hover:bg-card-elevated border-border hover:border-primary/20 transition-all duration-300 hover-lift"
            >
              <CardContent className="p-6 lg:p-8 text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <highlight.icon className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-heading text-xl font-medium text-heading mb-3">
                  {highlight.title}
                </h3>
                <p className="text-body font-body text-sm leading-relaxed">
                  {highlight.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Trust Note */}
        <div className="mt-12 text-center">
          <p className="text-caption font-body text-sm">
            Die hier zusammengefassten Eindrücke basieren auf dem allgemeinen Feedback meiner Patienten.
          </p>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
